import { FB_DELETE_CONTENT_TIMEOUT } from '$extensionSrc/consts/stageMachineConsts'
import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { StageType, FBRecommendationKey } from '$extensionSrc/utils/enums'

import { StageDef } from '../automationMachineInterfaces'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  getFacebookDeleteBeginUpdateStageAction,
  getFacebookDeleteCleanupAction,
  getFacebookDeleteContentCategoryForMonthStageAction,
  getFacebookDeleteContentFinishStageAction,
  getFacebookLoadDeleteContentCategoryStageAction,
  getFacebookLoadOldestPostStageAction,
  getFacebookSaveOldestPostStageAction,
} from './fbContentUtils'
import { FacebookContentCategoryKey } from './fbUrls'

const getCurrentValueFn = () => () => ''

const getIsFulfilledFn = () => () => false
const settingsToContentType = {
  [FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS]:
    FacebookContentCategoryKey.POSTS_PHOTOS_VIDEOS,
  [FacebookSettingKey.DELETE_CHECKINS]: FacebookContentCategoryKey.CHECKINS,
  [FacebookSettingKey.DELETE_REVIEWS]: FacebookContentCategoryKey.REVIEWS,
}

const deletePostLabels = {
  [FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS]: ['recycle bin', 'trash'],
  [FacebookSettingKey.DELETE_CHECKINS]: ['recycle bin', 'trash'],
  [FacebookSettingKey.DELETE_REVIEWS]: ['delete'],
}

const deletePostModalTitles = {
  [FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS]: 'move to trash?',
  [FacebookSettingKey.DELETE_CHECKINS]: 'move to trash?',
  [FacebookSettingKey.DELETE_REVIEWS]: 'delete?',
}

const confirmDeleteLabels = {
  [FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS]: 'move to trash',
  [FacebookSettingKey.DELETE_CHECKINS]: 'move to trash',
  [FacebookSettingKey.DELETE_REVIEWS]: 'delete',
}

const deletePostPageTitles = {
  [FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS]:
    'your posts, photos and videos',
  [FacebookSettingKey.DELETE_CHECKINS]: 'check-ins',
  [FacebookSettingKey.DELETE_REVIEWS]: 'reviews',
}

const postTypes = {
  [FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS]: 'posts, photos and videos',
  [FacebookSettingKey.DELETE_CHECKINS]: 'check-ins',
  [FacebookSettingKey.DELETE_REVIEWS]: 'reviews',
}

const initialValuesSettingKey = FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS
const contentType = 'posts'
const mainPageCategory = FacebookContentCategoryKey.POSTS
const mainPageTitle = 'posts'

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: `GATHER_DELETE_POSTS_LOAD_ACTIVITY_LOG`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookLoadOldestPostStageAction(),
  },
  {
    type: StageType.ACTION,
    key: `GATHER_DELETE_POSTS_GET_OLDEST_ACTIVITY`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookSaveOldestPostStageAction(
      initialValuesSettingKey,
      FBRecommendationKey.FB_DELETE_POSTS,
    ),
  },
  {
    type: StageType.ACTION,
    key: `GATHER_DELETE_POSTS_CLEANUP`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_END,
    action: getFacebookDeleteCleanupAction(
      FacebookContentCategoryKey.POSTS,
      'posts',
    ),
  },
]

const getCantUndoFn = () => () => true

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: `UPDATE_DELETE_POSTS_LOAD_ACTIVITY_LOG`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookDeleteBeginUpdateStageAction(
      FBRecommendationKey.FB_DELETE_POSTS,
    ),
  },
  {
    type: StageType.ACTION,
    key: `UPDATE_DELETE_POSTS_GET_OLDEST_ACTIVITY`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_START,
    action: getFacebookSaveOldestPostStageAction(
      initialValuesSettingKey,
      FBRecommendationKey.FB_DELETE_POSTS,
    ),
  },
  // Create flat array by adding each stage definition directly
  ...Object.keys(settingsToContentType).flatMap((s: FacebookSettingKey) => [
    {
      type: StageType.ACTION,
      key: `UPDATE_LOAD_${s}`,
      timeout: FB_DELETE_CONTENT_TIMEOUT,
      action: getFacebookLoadDeleteContentCategoryStageAction(
        initialValuesSettingKey,
        settingsToContentType[s],
      ),
    },
    {
      type: StageType.ACTION,
      key: `UPDATE_MONTH_${s}`,
      timeout: FB_DELETE_CONTENT_TIMEOUT,
      action: getFacebookDeleteContentCategoryForMonthStageAction(
        settingsToContentType[s],
        s,
        FBRecommendationKey.FB_DELETE_POSTS,
        postTypes[s],
        deletePostPageTitles[s],
        deletePostLabels[s],
        deletePostModalTitles[s],
        confirmDeleteLabels[s],
        contentType,
        initialValuesSettingKey,
      ),
    },
  ]),
  {
    type: StageType.ACTION,
    key: `UPDATE_DELETE_POSTS_FINISH`,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_DELETE_END,
    action: getFacebookDeleteContentFinishStageAction(
      FBRecommendationKey.FB_DELETE_POSTS,
      initialValuesSettingKey,
      contentType,
      mainPageCategory,
      mainPageTitle,
    ),
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
